import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { responseHandle, errorHandle } from '../utils/callback-handle';
import LoadingOverlay from '../components/common/LoadingOverlay';
import { toast } from 'react-toastify';

const initialState:any = {
  user: null,
  settings: []
};

export const UserContext = React.createContext(initialState);

export const UserProvider = ({ children }) => {
    const TOKEN_COOKIE = '_ssoToken';
    const REFRESH_TOKEN_COOKIE = '_ssoRefreshToken'    

    const [currentUser, setCurrentUser] = useState(null);
    const [settings, setSettings] = useState(null);
    const [authState, setAuthState] = useState('LOGIN');
    const [isLoading, setLoading] = useState(true);

    const authApi = axios.create({
      baseURL: process.env.REACT_APP_AUTH_URL,
      headers: {
        'Content-Type': 'application/json',
        'VLR-Authorization': btoa(JSON.stringify({ "id": process.env.REACT_APP_ID }))
      }
    });    

    useEffect(() => {
      const hydrate = () => new Promise<void>((resolve, reject) => {
        //Load user in session. If no user or session expired try to refresh token;
        const query = new URLSearchParams(window.location.search);
        const verifyToken = query.get('token');
        
        const idToken = Cookies.get(TOKEN_COOKIE);
        if (idToken) {
          if (verifyToken) {  
            setAuthState('RELOAD');
            resolve();   
          } else {
            getUserDetails(idToken).then(() => {
              setAuthState('SIGNED_IN');
              resolve();
            }).catch(err => {
              reject(catchAuthState(err));
            });
          }   
        } else {
          if (verifyToken) {
            getUserVerify(verifyToken).then((result: any) => {
                // const expires = new Date(new Date().getTime() + 60 * 60 * 1000) //expires in 60 minutes 
                const minutes = result.ExpiresIn / 60;
                const expires = new Date(new Date().getTime() + minutes * 60 * 1000);
                Cookies.set(TOKEN_COOKIE, result.IdToken, { expires: expires, domain: process.env.REACT_APP_DOMAIN })    
                setAuthState('RELOAD');
                resolve();   
            }).catch(err => {           
              reject(catchAuthState(err));
            });   
          } else {
            setAuthState('REFRESH_TOKEN');
            reject('Token Expired');
          }
        }
      });
  
      hydrate().catch(err => {
        toast.error(err.message);
      }).finally(() => {
        setLoading(false);
      })
    }, []); // eslint-disable-line react-hooks/exhaustive-deps  


    // useEffect(() => {
    //   if (currentUser) {
    //     refreshSessionSchedule();
    //   }
    // }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps  


    const getUserDetails = (token) => new Promise<void>((resolve, reject) => {  
      //Load user details from server using access token;
      const headerToken = { 'Authorization': token }
      authApi.post('/user/detail', {}, { headers: headerToken }).then((response) => {
        //If success save user detail data in state;
        const data = responseHandle(response);
        setCurrentUser(data.user);
        setSettings(data.rootSettings);
        resolve();
      }).catch(err => {
        console.error('currentAuthenticatedUser', err);
        reject(err);
      });
    });

    const getUserVerify = (token) => new Promise<string>((resolve, reject) => {  
      const headerToken = { 'Authorization': token }
      authApi.get('/user/verify', { headers: headerToken }).then((response) => {
        const data = responseHandle(response);
        resolve(data);
      }).catch(err => {
        console.error('verifyUser', err);
        reject(err);
      });
    });

    // const refreshSessionSchedule = () => {    
    //   const time = 2 * 60 * 1000; 
    //   setTimeout(async () => {
    //     await refreshSession(true);
    //     refreshSessionSchedule();
    //   }, time);  
    // };

    const catchAuthState = (err) => {
      if (err.response) {
        if (err.response.status === 402) {
          setAuthState('WAITING_FOR_ACCESS');
        } else
        if (err.response.status === 403) {
          setAuthState('ACCESS_DENIED');
        } else
        if (err.response.status === 401) {
          setAuthState('REFRESH_TOKEN');
        }          
      }          
      return errorHandle(err);
    }

    const signOut = async () => {
      try {
        Cookies.remove(TOKEN_COOKIE, { path: '/', domain: process.env.REACT_APP_DOMAIN });
        setCurrentUser(null);
        setAuthState(null);
      }
      catch (e) {
        console.debug(e)
      }
    }
  
    const refreshSession = async (server = false) => {
      try {
        if (server) {
          const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE);
          const headerToken = { 'Authorization': refreshToken};
          const response = await authApi.get('/refresh-session', { headers: headerToken });
          console.log(new Date());
          const data = responseHandle(response);
          const minutes = data.ExpiresIn / 60;
          const expires = new Date(new Date().getTime() + minutes * 60 * 1000);
          Cookies.set(TOKEN_COOKIE, data.IdToken, { expires: expires, domain: process.env.REACT_APP_DOMAIN });          
        } else {
          Cookies.remove(TOKEN_COOKIE, { path: '/', domain: process.env.REACT_APP_DOMAIN });
          setCurrentUser(null);
          setAuthState('REFRESH_TOKEN');  
        }
      } catch (e) {
        console.debug(e)
      }
    }
    

    const renderStore = isLoading ?
    <LoadingOverlay open={true} />
    :
    <UserContext.Provider value={{
        currentUser,
        settings,
        authState,
        refreshSession,
        signOut
        }}>
        {children}
      </UserContext.Provider>;

  return renderStore;

}    